import React, { useState, useEffect, useRef } from "react";
import { FaPaperclip, FaTrashAlt, FaTimesCircle, FaCheck, FaEllipsisV, FaFile, FaReply } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";
import { formatChatDate, formatChatTime, shouldShowDateSeparator } from "../../utils/dateUtils";
import "../CSS/ChatBox.css";
import io from "socket.io-client";

const AdminChatbox = ({ user, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [error, setError] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false); // New state for select mode
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [typing, setTyping] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // New state for reply
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);
  const textareaRef = useRef(null);
  const sendingLock = useRef(false);
  const [chatboxSize, setChatboxSize] = useState({ width: 380, height: 500 });
  const resizeRef = useRef({ isResizing: false, startX: 0, startY: 0, startWidth: 0, startHeight: 0 });

  // ... existing code ...

  // Auto-resize textarea
  const handleTextareaChange = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = textarea.scrollHeight;
    const maxHeight = 120; // Same as maxHeight in style
    textarea.style.height = newHeight > maxHeight ? maxHeight + 'px' : newHeight + 'px';
    setNewMessage(textarea.value);
    
    // Emit typing event to server
    socketRef.current.emit('typing', {
      userId: user._id,
      isTyping: textarea.value.length > 0
    });
  };

  useEffect(() => {
    // Initialize socket connection
    socketRef.current = io(process.env.REACT_APP_API_BASE_URL);
    
    // Join admin room
    socketRef.current.emit('joinRoom', 'admin');
    
    // Listen for new messages
    socketRef.current.on('newMessage', (message) => {
      if (message.userId === user._id) {
        setMessages(prevMessages => [...prevMessages, message]);
      }
    });
    
    // Listen for typing indicators
    socketRef.current.on('userTyping', ({ userId, isTyping }) => {
      if (userId === user._id) {
        setTyping(isTyping);
      }
    });

    // Cleanup on component unmount
    return () => {
      socketRef.current.disconnect();
    };
  }, [user._id]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen) {
        const headerActions = document.querySelector('.chatbox-header-actions');
        const menuDropdown = document.querySelector('.chatbox-menu-dropdown');

        // If click is outside both the header actions and the dropdown, close menu
        if (headerActions && menuDropdown &&
            !headerActions.contains(event.target) &&
            !menuDropdown.contains(event.target)) {
          setIsMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!user) return;

    const fetchChatHistory = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/chat/history?userId=${user._id}`
        );
        if (!response.ok) throw new Error("Failed to fetch chat history.");
        const data = await response.json();
        setMessages(data);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchChatHistory();

  }, [user]);

  const handleReply = (message) => {
    setReplyingTo(message);
    setNewMessage(""); // Clear current message when starting reply
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleMessageSelect = (messageId) => {
    if (selectedMessages.includes(messageId)) {
      setSelectedMessages(selectedMessages.filter((id) => id !== messageId));
    } else {
      setSelectedMessages([...selectedMessages, messageId]);
    }
  };

  const handleEnterSelectMode = () => {
    setSelectMode(true);
    setIsMenuOpen(false);
  };

  const handleExitSelectMode = () => {
    setSelectMode(false);
    setSelectedMessages([]);
    setIsMenuOpen(false);
  };

  const handleClose = () => {
    setSelectMode(false);
    setSelectedMessages([]);
    onClose();
  };

  // ... existing code ...

  const handleDeleteSelectedMessages = async () => {
    if (window.confirm("Are you sure you want to delete the selected messages?")) {
      const token = localStorage.getItem("adminToken");

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/chat/delete-messages`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ messageIds: selectedMessages }),
          }
        );

        if (!response.ok) throw new Error("Failed to delete selected messages");

        setMessages(messages.filter((msg) => !selectedMessages.includes(msg._id)));
        setSelectedMessages([]);
        setSelectMode(false); // Exit select mode after deletion
        setIsMenuOpen(false);
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleClearChat = async () => {
    if (window.confirm("Are you sure you want to clear the chat history? This action cannot be undone.")) {
      const token = localStorage.getItem("adminToken");

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/chat/clear-chat/${user._id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) throw new Error("Failed to clear chat history");

        setMessages([]);
        setSelectMode(false); // Exit select mode if active
        setIsMenuOpen(false);
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleMessageSend = async () => {
    // Prevent multiple sends by checking lock
    if (sendingLock.current) return;
    
    if (newMessage.trim() !== "" || selectedFiles.length > 0) {
      // Set lock to prevent multiple sends
      sendingLock.current = true;
      setIsSending(true);
      setSendProgress(10); // Start progress
      
      const messageText = newMessage.trim();
      
      // Clear message input immediately
      setNewMessage("");
      
      try {
        if (selectedFiles.length > 0) {
          setSendProgress(30);
          await uploadFiles();
          setSendProgress(70);
          if (messageText !== "") {
            // Show message instantly, then send to server in background
            showInstantMessage(messageText);
            await sendTextMessage(messageText);
          }
        } else {
          // Show message instantly, then send to server in background
          showInstantMessage(messageText);
          setSendProgress(50);
          await sendTextMessage(messageText);
        }
        setSendProgress(100);
        
        // Reset progress after completion
        setTimeout(() => {
          setIsSending(false);
          setSendProgress(0);
        }, 500);
      } catch (error) {
        setError("Failed to send message. Please try again.");
        setIsSending(false);
        setSendProgress(0);
      } finally {
        // Release lock after sending completes or fails
        sendingLock.current = false;
      }
    }
  };

  const showInstantMessage = (messageText) => {
    const instantMessage = {
      _id: `temp-${Date.now()}`,
      user: "Admin",
      message: messageText,
      sender: "admin",
      timestamp: new Date().toISOString(),
      userId: user._id,
      pending: true // Flag to style differently if needed
    };
    
    // Add message to UI immediately
    setMessages(prevMessages => [...prevMessages, instantMessage]);
    
    // Scroll to bottom immediately
    setTimeout(() => {
      scrollToBottom();
    }, 50);
  };

  const sendTextMessage = async (messageText) => {
    const newMessageObj = {
      user: "Admin",
      message: messageText,
      sender: "admin",
      timestamp: new Date().toISOString(),
      userId: user._id,
      replyTo: replyingTo ? replyingTo._id : null, // Include replyTo if replying
    };

    const tempId = `temp-${Date.now()}`;
    
    try {
      // Message already displayed by showInstantMessage
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/send`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify(newMessageObj),
      });

      if (!response.ok) throw new Error("Failed to send message.");

      const data = await response.json();
      
      // Update temporary message with real server data
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id.startsWith('temp-') && msg.message === messageText ? 
          { ...data, pending: false } : msg
        )
      );
      
      // Clear message input after successful send
      setNewMessage("");
      setReplyingTo(null); // Clear reply state after send
      
      // Reset textarea height properly
      if (textareaRef.current) {
        textareaRef.current.style.height = '35px'; // Set to min-height explicitly
      }
      
      // No need to set newMessage to empty here as we did it earlier
    } catch (error) {
      // Mark message as failed rather than removing it
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id.startsWith('temp-') && msg.message === messageText ? 
          { ...msg, failed: true, pending: false } : msg
        )
      );
      setError(error.message);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    // Emit typing event to server
    socketRef.current.emit('typing', {
      userId: user._id,
      isTyping: value.length > 0
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
    }
  };

  const removeSelectedFile = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
    if (updatedFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadFiles = async () => {
    const token = localStorage.getItem("adminToken");
    const formData = new FormData();
    
    // Add temporary file messages with pending status
    const tempMessages = selectedFiles.map(file => ({
      _id: `temp-file-${Date.now()}-${file.name}`,
      user: "Admin",
      message: `File uploaded: ${file.originalname || file.name}`,
      sender: "admin",
      timestamp: new Date().toISOString(),
      originalName: file.name,
      userId: user._id,
      pending: true
    }));
    
    // Show files as pending in UI
    setMessages(prevMessages => [...prevMessages, ...tempMessages]);
    
    selectedFiles.forEach(file => {
      formData.append("files", file);
    });
    
    formData.append("user", "Admin");
    formData.append("sender", "admin");
    formData.append("userId", user._id);
    formData.append("replyTo", replyingTo ? replyingTo._id : ""); // Include replyTo if replying

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/upload`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload files.");
      }

      const data = await response.json();
      
      // Remove temporary messages and add actual ones
      setMessages(prevMessages => {
        const filteredMessages = prevMessages.filter(msg => !msg._id.startsWith('temp-file-'));
        return [...filteredMessages, ...data];
      });
      
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      // Mark temporary file messages as failed
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id.startsWith('temp-file-') ? 
          { ...msg, failed: true, pending: false } : msg
        )
      );
      setError(error.message);
    }
  };

  const handleMouseDown = (e) => {
    resizeRef.current.isResizing = true;
    resizeRef.current.startX = e.clientX;
    resizeRef.current.startY = e.clientY;
    resizeRef.current.startWidth = chatboxSize.width;
    resizeRef.current.startHeight = chatboxSize.height;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'nw-resize';
    document.body.style.userSelect = 'none';

    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!resizeRef.current.isResizing) return;

    const deltaX = e.clientX - resizeRef.current.startX;
    const deltaY = e.clientY - resizeRef.current.startY;

    // Invert deltaX to make left drag increase width and right drag decrease width
    const newWidth = Math.max(300, Math.min(800, resizeRef.current.startWidth - deltaX));
    // Invert deltaY to make up drag increase height and down drag decrease height
    const newHeight = Math.max(400, Math.min(800, resizeRef.current.startHeight - deltaY));

    setChatboxSize({ width: newWidth, height: newHeight });
  };

  const handleMouseUp = () => {
    resizeRef.current.isResizing = false;

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Save size preference to localStorage
    localStorage.setItem('adminChatboxSize', JSON.stringify(chatboxSize));
  };

  // Load saved size preference on component mount
  useEffect(() => {
    const savedSize = localStorage.getItem('adminChatboxSize');
    if (savedSize) {
      try {
        const parsedSize = JSON.parse(savedSize);
        // Ensure constraints are respected
        const constrainedSize = {
          width: Math.max(300, Math.min(800, parsedSize.width || 380)),
          height: Math.max(400, Math.min(800, parsedSize.height || 500))
        };
        setChatboxSize(constrainedSize);
      } catch (error) {
        console.error('Error parsing saved admin chatbox size:', error);
      }
    }
  }, []);

  const truncateFileName = (name) => {
    if (name.length > 20) {
      return `${name.substring(0, 10)}...${name.substring(name.length - 7)}`;
    }
    return name;
  };

  return (
    <div className="chatbox" style={{ width: chatboxSize.width, height: chatboxSize.height }}>
      <div className={`chatbox-header ${selectMode ? 'select-mode' : ''}`}>
        <div className="chatbox-user-info">
          <div className="chatbox-user-avatar">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="chatbox-user-details">
            <h5>{user.name}</h5>
            <small>Active now</small>
          </div>
          {selectMode && (
            <div className="select-mode-indicator">
              {selectedMessages.length} selected
            </div>
          )}
        </div>
        <div className="chatbox-header-actions">
          <button 
            className="chatbox-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <FaEllipsisV />
          </button>
          {isMenuOpen && (
            <div className="chatbox-menu-dropdown">
              <button onClick={handleClearChat}>
                <FaTrashAlt /> Clear Chat
              </button>
              {!selectMode ? (
                <button onClick={handleEnterSelectMode}>
                  <FaCheck /> Select Messages
                </button>
              ) : (
                <>
                  {selectedMessages.length > 0 && (
                    <button onClick={handleDeleteSelectedMessages}>
                      <FaTrashAlt /> Delete Selected ({selectedMessages.length})
                    </button>
                  )}
                  <button onClick={handleExitSelectMode}>
                    <FaTimesCircle /> Exit Select Mode
                  </button>
                </>
              )}
              <button onClick={handleClose}>
                <FaTimesCircle /> Close Chat
              </button>
            </div>
          )}
          <button onClick={handleClose} className="close-chatbox-btn">
            &times;
          </button>
        </div>
      </div>
      
      <div className="chatbox-body">
        {error && <div className="chat-error">{error}</div>}
        
        {messages.map((msg, index) => {
          const showDateSeparator = shouldShowDateSeparator(msg, messages[index - 1]);
          
          return (
            <React.Fragment key={index}>
              {showDateSeparator && (
                <div className="chat-date-divider">
                  <span>{formatChatDate(msg.timestamp)}</span>
                </div>
              )}
              <div className={`message-container ${msg.sender} ${msg.pending ? 'pending' : ''} ${msg.failed ? 'failed' : ''} ${selectMode ? 'select-mode' : ''}`}>
                <div className={`message ${msg.sender} ${msg.replyTo ? 'reply' : ''}`}>
                  {selectMode && !msg.pending && !msg.failed && (
                    <input
                      type="checkbox"
                      checked={selectedMessages.includes(msg._id)}
                      onChange={() => handleMessageSelect(msg._id)}
                      className="message-checkbox"
                    />
                  )}
                  <div className="message-content">
                    {msg.replyTo && msg.replyTo.user && msg.replyTo.message && (
                      <div className="reply-preview">
                        <small>Replying to {msg.replyTo.user}: {msg.replyTo.message?.substring(0, 50)}...</small>
                      </div>
                    )}
                    {msg.fileUrl ? (
                      <div className="message-file">
                        <a
                          href={`${process.env.REACT_APP_API_BASE_URL}/uploads/chat/${msg.fileUrl.split('/').pop()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="file-link"
                        >
                          <div className="file-preview">
                            <div className="file-icon">
                              <FaFile />
                            </div>
                            <div className="file-info">
                              <span className="file-name">{truncateFileName(msg.originalName || '')}</span>
                              <span className="file-size">Click to download</span>
                            </div>
                          </div>
                        </a>
                      </div>
                    ) : (
                      <p>{msg.message.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                          {line}
                          {index < msg.message.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}</p>
                    )}
                    <div className="message-meta">
                      <div className="message-time">
                        {msg.pending ? 'Sending...' : msg.failed ? 'Failed to send' : formatChatTime(msg.timestamp)}
                      </div>
                      {msg.sender === "admin" && !msg.pending && !msg.failed && (
                        <span className="message-status">
                          <FaCheck />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="message-actions">
                    <button onClick={() => handleReply(msg)} className="reply-btn">
                      <FaReply />
                    </button>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        
        {typing && <div className="typing-indicator">User is typing...</div>}
        
        {isSending && (
          <div className="send-progress-container">
            <div 
              className="send-progress-bar" 
              style={{ width: `${sendProgress}%` }}
            ></div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="chatbox-footer">
        {replyingTo && (
          <div className="reply-preview-container">
            <div className="reply-preview">
              <small>Replying to: {replyingTo.user} - {replyingTo.message?.substring(0, 100)}...</small>
              <button onClick={cancelReply} className="cancel-reply-btn">
                <FaTimesCircle />
              </button>
            </div>
          </div>
        )}
        {selectedFiles.length > 0 && (
          <div className="file-preview-container">
            {selectedFiles.map((file, index) => (
              <div key={index} className="file-preview">
                <FaFile className="file-icon" />
                <span className="file-name">{truncateFileName(file.name)}</span>
                <button 
                  onClick={() => removeSelectedFile(index)} 
                  className="remove-file-btn"
                >
                  <FaTimesCircle />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="chatbox-input-container">
          <label htmlFor="file-upload" className="file-upload-label">
            <FaPaperclip />
            <input
              id="file-upload"
              type="file"
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: "none" }}
              multiple
            />
          </label>
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={handleTextareaChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleMessageSend();
              }
            }}
            placeholder="Type a message..."
            className="chatbox-input"
            rows="1"
            style={{ resize: 'none', overflow: 'auto', maxHeight: '120px' }}
          />
          <button 
            onClick={handleMessageSend} 
            className="send-btn"
            disabled={isSending || (!newMessage.trim() && selectedFiles.length === 0)}
          >
            <IoMdSend />
          </button>
        </div>
      </div>
      <div className="chatbox-resize-handle" onMouseDown={handleMouseDown}></div>
    </div>
  );
};

export default AdminChatbox;